
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { setCurrentSupabaseOrganization } from "@/lib/supabaseOrganization";
import { resolveCampaignStatusAndProgress } from "@/hooks/useCampaigns";
import { useCampaigns } from "@/hooks/useCampaigns";

export interface DashboardCampaign {
  id: string;
  name: string;
  brand: string;
  status: string;
  displayStatus: string;
  startDate: string | null;
  endDate: string | null;
  influencers: number;
  reach: number;
  engagement: number | null;
}

export function useDashboardCampaigns() {
  const { currentOrganization } = useOrganization();
  const { data: campaigns = [] } = useCampaigns();

  return useQuery({
    queryKey: ["dashboard-campaigns", currentOrganization?.id],
    queryFn: async (): Promise<DashboardCampaign[]> => {
      if (!currentOrganization?.id) {
        throw new Error('No current organization selected');
      }

      await setCurrentSupabaseOrganization(currentOrganization.id);

      const { data, error } = await supabase.rpc('get_dashboard_campaigns');
      if (error) throw error;
      if (!data) return [];

      return (data as unknown as Array<{
        id: string;
        name: string;
        brand: string;
        status: string;
        start_date: string | null;
        end_date: string | null;
        created_at: string;
        influencer_count: number;
        total_reach: number;
        avg_engagement: number | null;
      }>).map(row => {
        const resolvedStartDate = row.start_date || row.created_at;
        let resolvedEndDate = row.end_date;
        if (!resolvedEndDate && resolvedStartDate) {
          const fallbackEndDate = new Date(resolvedStartDate);
          if (!Number.isNaN(fallbackEndDate.getTime())) {
            fallbackEndDate.setDate(fallbackEndDate.getDate() + 30);
            resolvedEndDate = fallbackEndDate.toISOString().split('T')[0];
          }
        }

        const lifecycle = resolveCampaignStatusAndProgress(
          resolvedStartDate,
          resolvedEndDate,
          row.status,
        );
        const campaignStateFromList = campaigns.find((campaign) => campaign.id === row.id);

        return {
          id: row.id,
          name: row.name,
          brand: row.brand,
          status: lifecycle.status,
          displayStatus: lifecycle.status,
          startDate: resolvedStartDate,
          endDate: resolvedEndDate,
          influencers: row.influencer_count,
          reach: row.total_reach,
          engagement: row.avg_engagement != null
            ? Math.round(row.avg_engagement * 100) / 100
            : null,
        };
      });
    },
    enabled: !!currentOrganization?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
